import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import { Icon, TgHeader, TabBar } from "../components/ui/index.js";

interface QrResponse {
  payload: string;
  token: string;
  date: string;
  dataUrl: string;
  expiresAt: string;
  hasReadyPickup: boolean;
  readyPickupCount: number;
}

interface TemplateSummary {
  id: string;
  name: string;
  emoji: string | null;
}

function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

function maskPassport(series: string | null | undefined, number: string | null | undefined): string {
  // BUG-03: passport fields are nullable, and decryptPii degrades to null on key
  // rotation / corrupt ciphertext instead of throwing. Calling .slice on null would
  // throw and white-screen the whole profile screen (which also hosts the pickup QR).
  if (!number) return "Не указан";
  const last2 = number.slice(-2);
  return series ? `${series} · ••••${last2}` : `••••${last2}`;
}

function QrMini({ dataUrl, onClick }: { dataUrl: string; onClick: () => void }) {
  return (
    <button type="button" className="pr-qr-mini" onClick={onClick} aria-label="Открыть мой QR">
      <img src={dataUrl} alt="" width={52} height={52} style={{ borderRadius: 10, background: "#fff", padding: 4 }} />
    </button>
  );
}

function TemplatesRow({ templates }: { templates: TemplateSummary[] }) {
  const nav = useNavigate();
  const count = templates.length;

  if (count === 0) {
    return (
      <button type="button" className="pr-tpl-row empty" onClick={() => nav("/templates")}>
        <div className="pr-tpl-icon-empty">
          <Icon name="bookmark" size={18} />
        </div>
        <div className="pr-tpl-text">
          <p className="pr-tpl-title">Шаблоны</p>
          <p className="pr-tpl-sub">Сохраните часто используемых получателей</p>
        </div>
        <span className="pr-tpl-plus">+</span>
      </button>
    );
  }

  const preview = templates.slice(0, 3);
  const namesPreview = preview.map((t) => t.name).join(" · ");

  return (
    <button type="button" className="pr-tpl-row" onClick={() => nav("/templates")}>
      <div className="pr-tpl-emo-stack">
        {preview.map((t) => (
          <span key={t.id}>{t.emoji || "📦"}</span>
        ))}
      </div>
      <div className="pr-tpl-text">
        <p className="pr-tpl-title">
          Шаблоны
          <span className="pr-tpl-count">{count}</span>
        </p>
        <p className="pr-tpl-sub">{namesPreview}</p>
      </div>
      <Icon name="chevronRight" size={16} color="var(--mnp-fg-3)" />
    </button>
  );
}

export function ProfileScreen() {
  const nav = useNavigate();
  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => api<any>("/profile"),
  });
  const { data: qr } = useQuery({
    queryKey: ["me", "qr"],
    queryFn: () => api<QrResponse>("/me/qr"),
    staleTime: 60_000,
  });
  const { data: templates } = useQuery({
    queryKey: ["parcel-templates"],
    queryFn: () => api<TemplateSummary[]>("/parcel-templates"),
    staleTime: 60_000,
  });

  if (!profile) {
    return (
      <div className="app-shell">
        <TgHeader title="Профиль" />
        <div className="screen pad">
          <p className="t-body">Загрузка…</p>
        </div>
        <TabBar />
      </div>
    );
  }

  const hasActivePickup = qr?.hasReadyPickup ?? false;
  const statsTotal = profile.stats?.totalOrders ?? 0;
  const emptyStats = statsTotal === 0;

  return (
    <div className="app-shell">
      <TgHeader title="Профиль" />
      <div className="screen pad" style={{ paddingBottom: 24 }}>
        {/* Header */}
        <div className="pr-prof-head">
          <div className="avatar" style={{ width: 56, height: 56, fontSize: 18 }}>
            {initials(profile.fullName)}
          </div>
          <div className="pr-prof-head-text">
            <p className="name">{profile.fullName}</p>
            <p className="meta">
              {profile.username ? `@${profile.username} · ` : ""}
              {profile.phone}
            </p>
          </div>
          {!hasActivePickup && qr?.dataUrl && (
            <QrMini dataUrl={qr.dataUrl} onClick={() => nav("/profile/qr")} />
          )}
        </div>

        {/* Большой QR-блок — когда есть заказ к выдаче */}
        {hasActivePickup && qr?.dataUrl && (
          <div className="pr-qr-block">
            <span className="pr-qr-pill">
              {qr.readyPickupCount === 1
                ? "1 посылка готова к выдаче"
                : `${qr.readyPickupCount} посылки готовы к выдаче`}
            </span>
            <p className="pr-qr-block-title">Покажите оператору</p>
            <div className="pr-qr-frame">
              <img src={qr.dataUrl} alt="Ваш QR" width={200} height={200} />
            </div>
            <p className="pr-qr-block-sub">Покажите этот код оператору в ПВЗ</p>
          </div>
        )}

        {/* Мои данные */}
        <div className="card pr-card-flat">
          <div className="head">
            <p className="ttl">Мои данные</p>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => nav("/profile/edit")}
              style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <Icon name="edit" size={13} /> Править
            </button>
          </div>
          <div className="pr-data-row">
            <Icon name="user" size={16} color="var(--mnp-fg-3)" />
            <div className="pr-data-text">
              <p className="pr-data-k">ФИО</p>
              <p className="pr-data-v">{profile.fullName}</p>
            </div>
          </div>
          <div className="pr-data-row">
            <Icon name="phone" size={16} color="var(--mnp-fg-3)" />
            <div className="pr-data-text">
              <p className="pr-data-k">Телефон</p>
              <p className="pr-data-v">{profile.phone}</p>
            </div>
          </div>
          <div className="pr-data-row last">
            <Icon name="creditCard" size={16} color="var(--mnp-fg-3)" />
            <div className="pr-data-text">
              <p className="pr-data-k">Паспорт</p>
              <p className="pr-data-v">
                {maskPassport(profile.passportSeries, profile.passportNumber)}
              </p>
            </div>
          </div>
        </div>

        {/* Шаблоны — горизонтальная плашка */}
        <TemplatesRow templates={templates ?? []} />

        {/* Статистика */}
        <div className="card pr-card-flat">
          <p className="ttl" style={{ margin: "0 0 8px" }}>
            Статистика
          </p>
          <p className="t-small" style={{ margin: 0, color: "var(--mnp-fg-3)" }}>
            Всего заказов
          </p>
          <p className={"pr-stat-num" + (emptyStats ? " zero" : "")}>{statsTotal}</p>
        </div>
      </div>
      <TabBar />
    </div>
  );
}
